/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getArrayDecoder,
  getArrayEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type EncodedAccount,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type ReadonlyUint8Array,
} from '@solana/kit';
import {
  getVestingAllocationDecoder,
  getVestingAllocationEncoder,
  getVestingScheduleDecoder,
  getVestingScheduleEncoder,
  type VestingAllocation,
  type VestingAllocationArgs,
  type VestingSchedule,
  type VestingScheduleArgs,
} from '../types';

export const VESTING_CONFIG_DISCRIMINATOR = new Uint8Array([
  0, 138, 71, 135, 26, 29, 43, 125,
]);

export function getVestingConfigDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    VESTING_CONFIG_DISCRIMINATOR,
  );
}

export type VestingConfig = {
  discriminator: ReadonlyUint8Array;
  launch: Address;
  baseMint: Address;
  baseTokenProgram: Address;
  initialSupply: bigint;
  totalAllocation: bigint;
  schedules: Array<VestingSchedule>;
  allocations: Array<VestingAllocation>;
  version: number;
  bump: number;
  scheduleCount: number;
  allocationCount: number;
  reserved: ReadonlyUint8Array;
};

export type VestingConfigArgs = {
  launch: Address;
  baseMint: Address;
  baseTokenProgram: Address;
  initialSupply: number | bigint;
  totalAllocation: number | bigint;
  schedules: Array<VestingScheduleArgs>;
  allocations: Array<VestingAllocationArgs>;
  version: number;
  bump: number;
  scheduleCount: number;
  allocationCount: number;
  reserved: ReadonlyUint8Array;
};

/** Gets the encoder for {@link VestingConfigArgs} account data. */
export function getVestingConfigEncoder(): FixedSizeEncoder<VestingConfigArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['launch', getAddressEncoder()],
      ['baseMint', getAddressEncoder()],
      ['baseTokenProgram', getAddressEncoder()],
      ['initialSupply', getU64Encoder()],
      ['totalAllocation', getU64Encoder()],
      ['schedules', getArrayEncoder(getVestingScheduleEncoder(), { size: 8 })],
      [
        'allocations',
        getArrayEncoder(getVestingAllocationEncoder(), { size: 16 }),
      ],
      ['version', getU8Encoder()],
      ['bump', getU8Encoder()],
      ['scheduleCount', getU8Encoder()],
      ['allocationCount', getU8Encoder()],
      ['reserved', fixEncoderSize(getBytesEncoder(), 60)],
    ]),
    (value) => ({ ...value, discriminator: VESTING_CONFIG_DISCRIMINATOR }),
  );
}

/** Gets the decoder for {@link VestingConfig} account data. */
export function getVestingConfigDecoder(): FixedSizeDecoder<VestingConfig> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['launch', getAddressDecoder()],
    ['baseMint', getAddressDecoder()],
    ['baseTokenProgram', getAddressDecoder()],
    ['initialSupply', getU64Decoder()],
    ['totalAllocation', getU64Decoder()],
    ['schedules', getArrayDecoder(getVestingScheduleDecoder(), { size: 8 })],
    [
      'allocations',
      getArrayDecoder(getVestingAllocationDecoder(), { size: 16 }),
    ],
    ['version', getU8Decoder()],
    ['bump', getU8Decoder()],
    ['scheduleCount', getU8Decoder()],
    ['allocationCount', getU8Decoder()],
    ['reserved', fixDecoderSize(getBytesDecoder(), 60)],
  ]);
}

/** Gets the codec for {@link VestingConfig} account data. */
export function getVestingConfigCodec(): FixedSizeCodec<
  VestingConfigArgs,
  VestingConfig
> {
  return combineCodec(getVestingConfigEncoder(), getVestingConfigDecoder());
}

export function decodeVestingConfig<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<VestingConfig, TAddress>;
export function decodeVestingConfig<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<VestingConfig, TAddress>;
export function decodeVestingConfig<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
): Account<VestingConfig, TAddress> | MaybeAccount<VestingConfig, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getVestingConfigDecoder(),
  );
}

export async function fetchVestingConfig<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<VestingConfig, TAddress>> {
  const maybeAccount = await fetchMaybeVestingConfig(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeVestingConfig<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<VestingConfig, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeVestingConfig(maybeAccount);
}

export async function fetchAllVestingConfig(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<VestingConfig>[]> {
  const maybeAccounts = await fetchAllMaybeVestingConfig(
    rpc,
    addresses,
    config,
  );
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeVestingConfig(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<VestingConfig>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) => decodeVestingConfig(maybeAccount));
}

export function getVestingConfigSize(): number {
  return 1208;
}
